import { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
  Edit2,
  X,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  supportApiFetch,
  slugifyProductName,
  getArticles,
  getAdminTickets,
  getAdminLiveChatSessions,
} from "./supportApi";
import SupportConfirmationModal from "./SupportConfirmationModal";

export default function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    category: null,
  });
  /** null until the counts land, so the dialog opens immediately either way. */
  const [deleteUsage, setDeleteUsage] = useState(null);
  const [newCategoryParentId, setNewCategoryParentId] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryParentId, setEditCategoryParentId] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch categories
  const fetchCategories = async ({ silent = false } = {}) => {
    try {
      if (!silent) setIsLoading(true);
      const response = await supportApiFetch(
        "/api/v1/admin/products?page=1&limit=100"
      );
      const data = await response.json();
      setCategories(data.products || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    } finally {
      setIsLoading(false);
    }
  };

  const refetchCategories = () => fetchCategories({ silent: true });

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, category: null });
    setDeleteUsage(null);
  };

  /**
   * How much content a delete would strand.
   *
   * The API only refuses a delete that has child *products*; articles, tickets and chat
   * sessions reference a product by a bare `productSection`/`product` string with no
   * foreign key, so they survive the delete and simply stop matching any filter — present
   * in the database, reachable from nowhere. Nothing prevents that, so at least say it.
   *
   * Counts are advisory: the dialog opens straight away and a failure here leaves the
   * plain message rather than blocking the delete.
   */
  const openDeleteModal = async (category) => {
    setDeleteModal({ isOpen: true, category });
    setDeleteUsage(null);
    try {
      // limit=1 — only the totals are wanted. Note chats filter on `product`, not
      // `productSection`; sending the wrong one is ignored and counts everything.
      const [articles, tickets, chats] = await Promise.all([
        getArticles(1, 1, category.slug),
        getAdminTickets({ page: 1, limit: 1, productSection: category.slug }),
        getAdminLiveChatSessions({ page: 1, limit: 1, product: category.slug }),
      ]);
      setDeleteUsage({
        slug: category.slug,
        articles: articles?.pagination?.total || 0,
        tickets: tickets?.total || 0,
        chats: chats?.total || 0,
      });
    } catch (error) {
      console.error("Error counting product usage:", error);
    }
  };

  // Create category
  const handleCreateCategory = async (e) => {
    e.preventDefault();

    if (!newCategoryName.trim()) {
      toast.error("Category name is required");
      return;
    }

    const slug = slugifyProductName(newCategoryName);
    if (!slug) {
      toast.error("Name must contain at least one letter or number");
      return;
    }

    setIsCreating(true);
    try {
      const response = await supportApiFetch("/api/v1/admin/products", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          slug,
          name: newCategoryName.trim(),
          // Omitted rather than sent empty: the API rejects parentId: "".
          ...(newCategoryParentId ? { parentId: newCategoryParentId } : {}),
        }),
      });

      if (response.ok) {
        await response.json();
        toast.success("Category created successfully");
        setNewCategoryName("");
        setNewCategoryParentId("");
        refetchCategories();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to create category");
      }
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error("Failed to create category");
    } finally {
      setIsCreating(false);
    }
  };

  // Update category
  const handleUpdateCategory = async (e) => {
    e.preventDefault();

    if (!editCategoryName.trim()) {
      toast.error("Category name is required");
      return;
    }

    if (!editingCategory?.id) return;

    setIsUpdating(true);
    try {
      const response = await supportApiFetch(
        `/api/v1/admin/products/${editingCategory.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          // The slug is deliberately not sent. It used to be re-derived from the name
          // on every save, which silently renamed it — and `productSection` on articles,
          // tickets and article versions is a plain string with no foreign key, so every
          // one of them pointing at the old slug was orphaned with no warning.
          // ponytail: a slug typo therefore can't be fixed here any more; delete and
          // recreate, or PATCH the slug directly if it ever genuinely has to move.
          body: JSON.stringify({
            name: editCategoryName.trim(),
            // null detaches; "" would be rejected by the API.
            parentId: editCategoryParentId || null,
          }),
        }
      );

      if (response.ok) {
        await response.json();
        toast.success("Category updated successfully");
        setEditingCategory(null);
        setEditCategoryName("");
        setEditCategoryParentId("");
        refetchCategories();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to update category");
      }
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error("Failed to update category");
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete category
  const handleDeleteCategory = async () => {
    const id = deleteModal.category?.id;
    if (!id) return;

    setDeletingId(id);
    try {
      const response = await supportApiFetch(`/api/v1/admin/products/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Category deleted successfully");
        refetchCategories();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to delete category");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category");
    } finally {
      setDeletingId(null);
      closeDeleteModal();
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /**
   * The admin list is flat and carries parentId, so the nesting is rebuilt here.
   *
   * Anything whose parent is not in the list is shown at the top level rather than
   * dropped — hiding a product is the very bug this page is being fixed for.
   */
  const rootIds = new Set(categories.filter((c) => !c.parentId).map((c) => c.id));
  const roots = categories.filter((c) => !c.parentId || !rootIds.has(c.parentId));
  const childrenOf = (id) => categories.filter((c) => c.parentId === id);

  /** Render order: each root immediately followed by its own children. */
  const ordered = roots.flatMap((root) => [
    { ...root, isChild: false },
    ...childrenOf(root.id).map((child) => ({ ...child, isChild: true })),
  ]);

  /**
   * Only roots may be chosen as a parent, and never the row being edited.
   *
   * That keeps the tree two levels deep, which is the depth the public products API
   * actually returns — a grandchild would show up here and be invisible to every
   * customer. Excluding self also pre-empts the API's "cannot be its own parent" error.
   */
  const parentOptions = (excludeId) =>
    categories.filter((c) => !c.parentId && c.id !== excludeId);

  /**
   * What the delete dialog says.
   *
   * The slug check drops a count that arrived for a product the dialog has since moved
   * off, so a slow response can never describe the wrong delete.
   */
  const deleteMessage = (() => {
    const name = deleteModal.category?.name;
    const plain = `Are you sure you want to delete the category "${name}"? This action cannot be undone.`;
    if (!deleteUsage || deleteUsage.slug !== deleteModal.category?.slug) {
      return plain;
    }
    const parts = [
      [deleteUsage.articles, "article"],
      [deleteUsage.tickets, "ticket"],
      [deleteUsage.chats, "chat session"],
    ]
      .filter(([count]) => count > 0)
      .map(([count, word]) => `${count} ${word}${count === 1 ? "" : "s"}`);

    if (parts.length === 0) return plain;
    return `Deleting "${name}" will leave ${parts.join(", ")} filed under "${deleteUsage.slug}" with no product. They are not deleted, but they stop matching every product filter. This cannot be undone.`;
  })();

  if (isLoading) {
    return (
      <div className="">
        <div className="py-10 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Categories <span className="text-gradient-ginox">Management</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          Create, edit, and remove article categories
        </p>
      </div>

      <div className="max-w-4xl">
        <div className="rounded-lg border border-border/70 bg-card p-5 space-y-6">
          {/* Create Category Form */}
          <form onSubmit={handleCreateCategory} className="space-y-2">
            <div className="flex gap-2">
            <input
              type="text"
              placeholder="Enter category name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="flex-1 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              disabled={isCreating}
            />
            <select
              value={newCategoryParentId}
              onChange={(e) => setNewCategoryParentId(e.target.value)}
              className="w-44 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              disabled={isCreating}
              aria-label="Parent product"
            >
              <option value="">No parent (top level)</option>
              {parentOptions(null).map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parent.name}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={isCreating || !newCategoryName.trim()}
              className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {isCreating ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5" />
                  Add Category
                </>
              )}
            </button>
            </div>
            {/* The slug is set once and never changes again, so show it before it is
                committed rather than after. */}
            {newCategoryName.trim() && (
              <p className="text-[11px] text-muted-foreground/70">
                Slug:{" "}
                <span className="font-mono text-foreground">
                  {slugifyProductName(newCategoryName) || "—"}
                </span>{" "}
                — permanent; articles and tickets are filed against it.
              </p>
            )}
          </form>

          {/* Categories List */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
              Products ({roots.length}) &middot; Sub-products{" "}
              ({ordered.length - roots.length})
            </p>

            {categories.length === 0 ? (
              <div className="flex items-start gap-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <AlertCircle className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0" aria-hidden="true" />
                <p className="text-yellow-400 text-sm">
                  No categories found. Create your first category above.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {ordered.map((category) => (
                  <div
                    key={category.id}
                    className={`flex items-center justify-between px-4 py-3 rounded-md border border-border/40 bg-white/[0.02] hover:border-border/70 transition-colors ${
                      category.isChild ? "ml-6" : ""
                    }`}
                  >
                    {editingCategory?.id === category.id ? (
                      // Edit mode
                      <form
                        onSubmit={handleUpdateCategory}
                        className="flex items-center gap-2 flex-1"
                      >
                        <input
                          type="text"
                          value={editCategoryName}
                          onChange={(e) => setEditCategoryName(e.target.value)}
                          className="flex-1 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                          disabled={isUpdating}
                          autoFocus
                        />
                        <select
                          value={editCategoryParentId}
                          onChange={(e) =>
                            setEditCategoryParentId(e.target.value)
                          }
                          // A product that has children cannot also be given a parent —
                          // that is the other way to reach depth 3, which the public API
                          // does not return and customers therefore never see.
                          disabled={
                            isUpdating || childrenOf(category.id).length > 0
                          }
                          title={
                            childrenOf(category.id).length > 0
                              ? "Has sub-products — move them out first"
                              : "Parent product"
                          }
                          className="w-40 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 disabled:opacity-40"
                          aria-label="Parent product"
                        >
                          <option value="">No parent (top level)</option>
                          {parentOptions(category.id).map((parent) => (
                            <option key={parent.id} value={parent.id}>
                              {parent.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="submit"
                          disabled={isUpdating || !editCategoryName.trim()}
                          className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-2.5 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isUpdating ? (
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingCategory(null);
                            setEditCategoryName("");
                            setEditCategoryParentId("");
                          }}
                          disabled={isUpdating}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </form>
                    ) : (
                      // View mode
                      <>
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm text-foreground">
                            {category.name}
                          </span>
                          {/* Shown because a rename no longer moves it. */}
                          <span className="font-mono text-[11px] text-muted-foreground/60">
                            {category.slug}
                          </span>
                          {childrenOf(category.id).length > 0 && (
                            <span className="rounded border border-border/70 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-muted-foreground">
                              {childrenOf(category.id).length} sub-products
                            </span>
                          )}
                          {/* This panel only ever creates two levels, so a third can
                              only arrive from a direct API call — and the public
                              products endpoint stops at two, which means customers
                              never see it at all. Worth saying out loud rather than
                              letting it sit here looking like any other product. */}
                          {category.parentId &&
                            !rootIds.has(category.parentId) && (
                              <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-400">
                                Hidden from customers
                              </span>
                            )}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingCategory(category);
                              setEditCategoryName(category.name);
                              setEditCategoryParentId(category.parentId || "");
                            }}
                            className="rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                            aria-label="Edit category"
                          >
                            <Edit2 className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(category)}
                            disabled={deletingId === category.id}
                            className="rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-xs text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Delete category"
                          >
                            {deletingId === category.id ? (
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <SupportConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteCategory}
        title="Delete Category"
        message={deleteMessage}
        confirmText="Yes, Delete"
        cancelText="No, Cancel"
        loading={deletingId !== null}
        loadingText="Deleting..."
      />
    </div>
  );
}
